import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import favoritesService from "../services/favoritesService";
import { isAuthenticated } from "../services/authService";

interface FavoritesContextValue {
  /** Set of favorited video IDs for the current user */
  favoritedIds: Set<string>;
  /** Returns true if the given video ID is in the user's favorites */
  isFavorited: (videoId: string) => boolean;
  /** Toggle a video favorite — optimistic update, rolls back on API error */
  toggle: (videoId: string) => Promise<void>;
  /** Whether the initial favorites list is still loading */
  loading: boolean;
}

const FavoritesContext = createContext<FavoritesContextValue | null>(null);

export const useFavorites = (): FavoritesContextValue => {
  const ctx = useContext(FavoritesContext);
  if (!ctx) throw new Error("useFavorites must be used inside <FavoritesProvider>");
  return ctx;
};

export const FavoritesProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [favoritedIds, setFavoritedIds] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(false);
  // Prevent double-fetch in React StrictMode
  const fetchedRef = useRef(false);

  useEffect(() => {
    if (!isAuthenticated() || fetchedRef.current) return;
    fetchedRef.current = true;

    const load = async () => {
      setLoading(true);
      try {
        const records = await favoritesService.getVideoFavorites();
        const ids = records.map((r) =>
          typeof r.video === "string" ? r.video : r.video._id
        );
        setFavoritedIds(new Set(ids));
      } catch {
        // Not critical — user just won't see pre-loaded state
      } finally {
        setLoading(false);
      }
    };

    load();
  }, []);

  const isFavorited = useCallback(
    (videoId: string) => favoritedIds.has(videoId),
    [favoritedIds]
  );

  const toggle = useCallback(async (videoId: string) => {
    // Optimistic update
    setFavoritedIds((prev) => {
      const next = new Set(prev);
      if (next.has(videoId)) {
        next.delete(videoId);
      } else {
        next.add(videoId);
      }
      return next;
    });

    try {
      // Sync with backend — it returns the actual final state
      const nowFavorited = await favoritesService.toggleVideo(videoId);
      // Reconcile: ensure our local state matches what the server says
      setFavoritedIds((prev) => {
        const next = new Set(prev);
        if (nowFavorited) {
          next.add(videoId);
        } else {
          next.delete(videoId);
        }
        return next;
      });
    } catch {
      // Roll back optimistic update on network/auth error
      setFavoritedIds((prev) => {
        const next = new Set(prev);
        if (next.has(videoId)) {
          next.delete(videoId);
        } else {
          next.add(videoId);
        }
        return next;
      });
    }
  }, []);

  return (
    <FavoritesContext.Provider value={{ favoritedIds, isFavorited, toggle, loading }}>
      {children}
    </FavoritesContext.Provider>
  );
};
